import {Common} from '../constants/common';
import {
  ContributionStatus,
} from '../constants/ContributionStatus';
import {
  EsppContributionType,
} from '../constants/EsppContributionType';
import {
  floorToDigits, round,
} from '../utils';
import {
  getValidDate,
} from './date';
import {
  convertToCents,
  convertToDollar,
} from './money';

const MONTH_PER_YEAR = 12;
export function calculateEquivalentShares(value, purchasePrice, plan = null) {
  const sharesPurchased = calculatePurchaseShares(value, purchasePrice);
  const nonFractionalShares = Math.abs(Math.floor(sharesPurchased));
  if (plan && plan.fractionalShares) {
    return floorToDigits(sharesPurchased, plan.fractionalDigits);
  }
  return nonFractionalShares;
}

// This function is used to calculate fair value
export function calculateFairValue(equivalentShares, grantDateFairValue) {
  return equivalentShares * grantDateFairValue;
}

// this function returns the actual shares purchased without applying any settings
export function calculatePurchaseShares(value, purchasePrice) {
  return value / purchasePrice;
}

// This function is used to calculate total fair value
export function calculateTotalFairValue(periods) {
  return periods.reduce((fairValue, period) => {
    return fairValue + calculateFairValue(period.equivalentShares, period.grantDateFairValue);
  }, 0);
}

export function calculateExpensePerDay(periods, offeringPeriod) {
  if (periods) {
    const totalFairValue = calculateTotalFairValue(periods);
    // Calculate Expense per day in cents
    if (totalFairValue && offeringPeriod) {
      return totalFairValue / offeringPeriod;
    }
  }
  return 0;
}
// This function return expense per day in cents
export function calculateExpensePerDayInCents(periods, offeringPeriod) {
  return convertToCents(calculateExpensePerDay(periods, offeringPeriod), false);
}


/**
 * this function returns purchase periods as single array
 * @param {*} data
 * @param {*} returnAsSingleArray
 * @param {*} mergeBlackScholes
 * @return {?}
 */
export function getPurchasePeriodsAsArray(data, returnAsSingleArray = true, mergeBlackScholes = true) {
  const resultArray = [];
  const blackScholeValues = {};
  const resultContributionArray = [];
  for (const key in data) {
    if (key.startsWith('pp_')) {
      const currentPeriod = data[key];
      if (returnAsSingleArray) {
        currentPeriod.map((current) => {
          blackScholeValues[`tranche${current.tranche}`] = current.blackSchole;
          current.periodStartdate = parseInt(key.replace('pp_', ''));
          resultContributionArray.push({
            ...current,
            purchasePrice: current.purchasePrice? current.purchasePrice : data.purchasePrice,
            object_id: data.object_id,
            purchaseExpiration: data.purchaseExpiration,
            offerStartdate: data.offerStartdate,
          });
        });
      } else {
        resultArray.push(currentPeriod);
      }
    }
  }
  resultContributionArray.map((current) => {
    let blackSchole = current.blackSchole;
    if (mergeBlackScholes) {
      blackSchole = blackScholeValues;
    }
    resultArray.push({
      ...current,
      blackSchole,
      purchaseExpiration: data.purchaseExpiration,
      offerStartdate: data.offerStartdate,
    });
  });
  // sort the array based on the period startdate
  resultArray.sort(function(a, b) {
    return a.periodStartdate - b.periodStartdate;
  });
  return resultArray;
}


// This function always take stockprice in dollars
export function calculatePurchasePrice(stockPrice, planDetail) {
  if (planDetail && planDetail.employeeDiscount > 0) {
    const discount = stockPrice * planDetail.employeeDiscount / 100;
    return stockPrice - discount;
  }
  return stockPrice;
}

export function calculateDollarAmountPerPurchasePeriod(dollarAmount, periodsPerAnnum) {
  return dollarAmount / periodsPerAnnum;
}

export function calculateDollarPercentPerPurchasePeriod(userSalary, percent) {
  return round(userSalary * percent / 100);
}

export function calculateMaxContribution(maxPlan, userSalary, purchasePeriod) {
  const periodsPerAnnum = MONTH_PER_YEAR / Number(purchasePeriod);
  let userSalaryPerPeriod = null;
  const calculateMaxValues = [];
  // calculate salary per purchase period
  if (userSalary) {
    userSalaryPerPeriod = userSalary / periodsPerAnnum;
    calculateMaxValues.push(userSalaryPerPeriod);
  }
  switch (maxPlan.type) {
    case EsppContributionType.PERCENT:
      if (userSalaryPerPeriod) {
        calculateMaxValues.push(calculateDollarPercentPerPurchasePeriod(userSalaryPerPeriod, maxPlan.percent));
      } else {
        const maxDollarValue = calculateDollarAmountPerPurchasePeriod(maxPlan.dollar, periodsPerAnnum);
        calculateMaxValues.push(calculateDollarPercentPerPurchasePeriod(maxDollarValue, maxPlan.percent));
      }
      break;
    case EsppContributionType.DOLLAR:
      calculateMaxValues.push(calculateDollarAmountPerPurchasePeriod(maxPlan.dollar, periodsPerAnnum));
      break;
    case EsppContributionType.BOTH:
      if (userSalaryPerPeriod) {
        calculateMaxValues.push(calculateDollarPercentPerPurchasePeriod(userSalaryPerPeriod, maxPlan.percent));
      } else {
        const maxDollarValue = calculateDollarAmountPerPurchasePeriod(maxPlan.dollar, periodsPerAnnum);
        calculateMaxValues.push(calculateDollarPercentPerPurchasePeriod(maxDollarValue, maxPlan.percent));
      }
      calculateMaxValues.push(calculateDollarAmountPerPurchasePeriod(maxPlan.dollar, periodsPerAnnum));
      break;
  }
  return Math.min(...calculateMaxValues);
}
export function calculateMinContribution(minPlan, userSalary, purchasePeriod) {
  const periodsPerAnnum = MONTH_PER_YEAR / Number(purchasePeriod);
  let userSalaryPerPeriod = null;
  if (userSalary) {
    userSalaryPerPeriod = userSalary / periodsPerAnnum;
  }
  const calculateMinValues = [];
  switch (minPlan.type) {
    case EsppContributionType.PERCENT:
      if (userSalaryPerPeriod) {
        calculateMinValues.push(calculateDollarPercentPerPurchasePeriod(userSalaryPerPeriod, minPlan.percent));
      } else {
        const minDollarValue = calculateDollarAmountPerPurchasePeriod(minPlan.dollar, periodsPerAnnum);
        calculateMinValues.push(calculateDollarPercentPerPurchasePeriod(minDollarValue, minPlan.percent));
      }
      break;
    case EsppContributionType.DOLLAR:
      calculateMinValues.push(calculateDollarAmountPerPurchasePeriod(minPlan.dollar, periodsPerAnnum));
      break;
    case EsppContributionType.BOTH:
      if (userSalaryPerPeriod) {
        calculateMinValues.push(calculateDollarPercentPerPurchasePeriod(userSalaryPerPeriod, minPlan.percent));
      } else {
        const minDollarValue = calculateDollarAmountPerPurchasePeriod(minPlan.dollar, periodsPerAnnum);
        calculateMinValues.push(calculateDollarPercentPerPurchasePeriod(minDollarValue, minPlan.percent));
      }
      calculateMinValues.push(calculateDollarAmountPerPurchasePeriod(minPlan.dollar, periodsPerAnnum));
      break;
  }
  return Math.min(...calculateMinValues);
}
// this function calculates the taabl refund due
export function calcuateTaablRefundDue(
    taablMaxShare,
    contributionAmount,
    fractionalShare,
    sharesPurchansed,
    purchasePrice,
    plan,
) {
  if (fractionalShare > taablMaxShare) {
    const taablRefundDue = contributionAmount - sharesPurchansed * purchasePrice;
    return {
      taablRefundDue,
      taablRefundReason: 'Max shares reached',
    };
  } else {
    const taablRefundDue = (fractionalShare - sharesPurchansed) * purchasePrice;
    if (taablRefundDue > 1) {
      return {
        taablRefundDue,
        taablRefundReason: 'Due to Fractional shares',
      };
    } else {
      return {
        taablRefundDue: 'NA',
        taablRefundReason: 'NA',
      };
    }
  }
}
// this function calculates taxes
export function calculateTaxes(contributionAmount, feeRate, taxRate) {
  return contributionAmount * feeRate * taxRate;
}
// This function tell whether contribution is done by taabl or user
export function isParticipating(contribution) {
  if (contribution.GSI1_sort_key.includes('|true')) {
    return true;
  }
  return false;
}

/**
 * This function takes contributions and all Periods to calculate the period wise profit summary
 * @param  {Array.<object>} userContribution - Contribution by users
 * @param  {Array.<object>} allPeriods - periods array
 * @return {Array.<object>}
 */
export function calculatePeriodwiseProfits(userContribution, allPeriods) {
  const response = {};
  const participating = isParticipating(userContribution);

  const purchasePrice = convertToDollar(userContribution.purchasePrice);
  const today = getValidDate();

  // get all the purchasePeriod data
  const contributionByPeriods = getAllPeriodData(userContribution, today);

  for (const periodStartDate in contributionByPeriods) {
    if (contributionByPeriods.hasOwnProperty(periodStartDate)) {
      const contributions = contributionByPeriods[periodStartDate];
      response[periodStartDate] = {
        periodStartDate:
        contributionByPeriods[periodStartDate][0].periodStartdate,
        periodEndDate: contributionByPeriods[periodStartDate][0].periodEnddate,
        totalDeduction: 0,
        totalValue: 0,
        profit: 0,
        loan: 0,
        totalLoanBalance: 0,
        shares: 0,
        purchasePrice,
        isParticipating: participating,
        stockPrice: convertToDollar(
            contributions[contributions.length - 1].stockPrice,
        ),
      };
      const oldPeriodData = allPeriods.find((p)=>p.periodStartdate === periodStartDate);
      // check if the oldPeriod has stock price field populated (to check if the period is closed)
      if (
        oldPeriodData &&
      oldPeriodData.stockPrice
      ) {
        response[periodStartDate].stockPrice =
      parseFloat(
          oldPeriodData.stockPrice,
      );
      }
      // Employee might have done increase/decrease/terminate/withdraw
      // First check for withdraw/terminate. if withdraw/terminate stats will be 0
      if (
        !contributionByPeriods[periodStartDate].find(
            (c) =>
              c.type === ContributionStatus.WITHDRAW ||
          c.type === ContributionStatus.TERMINATE,
        )
      ) {
      // This means employee has active contributions
      // We need to get total amount he is contributing after applying increase/decrease
        for (const contribution of contributionByPeriods[periodStartDate]) {
          let stats = {};
          if (
            oldPeriodData
          ) {
            stats = calculateContributionStat(
                {
                  ...contribution,
                },
                userContribution,
                oldPeriodData,
            );
          } else {
            stats = calculateContributionStat(
                {
                  ...contribution,
                },
                userContribution,
            );
          }
          response[periodStartDate].profit += stats.profit;
          response[periodStartDate].shares += stats.shares;
          response[periodStartDate].taablFee = stats.taablFee;
          response[periodStartDate].loan = stats.loan;
          response[periodStartDate].totalLoanBalance = stats.loan + stats.taablFee;
          response[periodStartDate].totalValue += convertToDollar(
              stats.totalValue,
          );
          if (!participating) {
            response[periodStartDate].totalDeduction += convertToDollar(
                stats.totalDeduction,
            );
          }
        }
      }
    }
  }

  return response;
}
export function getAllPeriodData(contribution, today) {
  const offerStartDate = contribution.offerStartdate;
  const purchasePeriods = {};
  let index = offerStartDate;
  while (contribution && `pp_${index}` in contribution) {
    const currentPeriod = contribution[`pp_${index}`];
    if (currentPeriod[0].periodStartdate < today) {
      purchasePeriods[index] = currentPeriod;
    }
    if (currentPeriod && currentPeriod.length > 0) {
      index = currentPeriod[0].periodEnddate + 1;
    } else {
      index = undefined;
    }
  }
  return purchasePeriods;
}
// This function is used to calcuate contribution stat like deduction, profit etc
export function calculateContributionStat(
    contributionByPeriod,
    userContributionDetails,
    oldPeriod = null,
) {
  const statValue = {
    totalDeduction: 0,
    totalValue: 0,
    profit: 0,
    shares: 0,
  };
  if (contributionByPeriod) {
    const shares = calculateEquivalentShares(
        contributionByPeriod.contributionValue,
        userContributionDetails.purchasePrice,
    );
    let stockPrice = 0;
    if (oldPeriod && 'stockPrice' in oldPeriod && oldPeriod.stockPrice) {
      stockPrice = convertToCents(oldPeriod.stockPrice);
    } else {
      stockPrice = userContributionDetails.stockPrice;
    }
    let currentValue = shares * stockPrice;
    const purchaseValue = contributionByPeriod.contributionValue;
    if (
      contributionByPeriod.stockPrice ===
      userContributionDetails.purchasePrice
    ) {
      currentValue = purchaseValue;
    }
    statValue.totalDeduction = purchaseValue;
    statValue.totalValue += currentValue;
    statValue.shares = shares;
    statValue.profit = convertToDollar(currentValue - purchaseValue);

    // If taabl is contributing then taabl fee is 6% of contribution amount
    // If taabl is not contributing then taabl fee is 1/3 of the profit
    if (isParticipating(userContributionDetails)) {
      statValue.loan = convertToDollar(purchaseValue);
      statValue.taablFee = statValue.loan * Common.TAABL_PROFIT_PARTICIPATING;
    } else {
      statValue.taablFee = statValue.profit * Common.TAABL_PROFIT_NON_PARTICIPATING;
    }
    statValue.profit = statValue.profit - statValue.taablFee;
  }
  console.log(statValue);
  return statValue;
}
